/**
 * Google Cloud — Integrace s Google Cloud MCP Toolbox
 * Správa připojení k Cloud SQL, Firestore a dalším službám, nebo přímé připojení k existující DB.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { mcpCall } from '../../lib/mcp';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusDot } from '../../components/ui/StatusDot';
import { Modal } from '../../components/ui/Modal';
import { SearchInput } from '../../components/ui/SearchInput';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Textarea } from '../../components/ui/Textarea';
import {
  ArrowRight,
  Cloud,
  Database,
  Plus,
  RefreshCw,
  Terminal,
  Trash2,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GoogleConnection {
  id: string;
  name: string;
  project_id: string;
  region: string;
  database_host?: string;
  database_port?: number;
  instance_id?: string;
  database_name?: string;
  enabled: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface GoogleQueryResult {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
}

type ConnectionType = 'google-cloud' | 'direct';

// ─── Helpers ────────────────────────────────────────────────────────────────


// ─── Connections Tab ────────────────────────────────────────────────────────

function ConnectionsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['google-cloud-connections'],
    queryFn: () =>
      api.mcp<{ ok: boolean; connections?: GoogleConnection[] }>('google-cloud.list'),
    staleTime: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mcp('google-cloud.disconnect', { connection_id: id }),
    onSuccess: () => {
      toast.success(t('googleCloud.connectionRemoved'));
      qc.invalidateQueries({ queryKey: ['google-cloud-connections'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const connections = data?.connections ?? [];
  const filtered = connections.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.project_id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-gray-500'>{t('googleCloud.connectionsCount', { count: connections.length })}</div>
        <div className='flex gap-2'>
          <SearchInput value={search} onChange={setSearch} placeholder={t('googleCloud.searchPlaceholder')} />
          <Button
            variant='primary'
            size='sm'
            onClick={() => setShowConnect(true)}
            icon={<Plus size={14} />}
          >
            {t('googleCloud.connect')}
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => refetch()}
            icon={<RefreshCw size={14} />}
          />
        </div>
      </div>

      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <RefreshCw className='animate-spin text-gray-600' />
          </div>
        )
        : filtered.length === 0
        ? (
          <EmptyState
            icon={<Cloud size={24} />}
            title={t('googleCloud.noConnections')}
            description={t('googleCloud.noConnectionsDesc')}
          />
        )
        : (
          <Card>
            <CardHeader>
              <CardTitle icon={Cloud}>{t('googleCloud.connectionsTitle', { count: filtered.length })}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-2'>
                {filtered.map((conn) => (
                  <div
                    key={conn.id}
                    className='flex items-center gap-3 p-3 rounded-xl border border-gray-800 bg-gray-900/20 hover:bg-gray-800/30 transition-colors'
                  >
                    <StatusDot status={conn.enabled ? 'running' : 'error'} />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='font-semibold text-white'>{conn.name}</span>
                        {conn.instance_id && (
                          <Badge variant='secondary'>
                            <Database size={10} /> {conn.instance_id}
                          </Badge>
                        )}
                        {conn.region === 'direct' && (
                          <Badge variant='success'>
                            {t('googleCloud.direct')}
                          </Badge>
                        )}
                      </div>
                      <div className='text-xs text-gray-500 mt-0.5'>
                        {conn.project_id} • {conn.region}
                        {conn.database_name && (
                          <span className='ml-2 text-blue-400'>{conn.database_name}</span>
                        )}
                        {conn.database_host && conn.database_port && (
                          <span className='ml-2 text-gray-400'>
                            {conn.database_host}:{conn.database_port}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='text-gray-400 hover:text-red-400'
                        title={t('googleCloud.delete')}
                        onClick={() => deleteMut.mutate(conn.id)}
                        disabled={deleteMut.isPending}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Create Connection Modal */}
      <CreateConnectionModal open={showConnect} onClose={() => setShowConnect(false)} />
    </div>
  );
}

// ─── Create Connection Modal with Wizard ────────────────────────────────────

function CreateConnectionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [connectionType, setConnectionType] = useState<ConnectionType>('google-cloud');

  // Google Cloud form state
  const [googleName, setGoogleName] = useState('');
  const [googleProjectId, setGoogleProjectId] = useState('');
  const [googleRegion, setGoogleRegion] = useState('europe-west1');
  const [googleInstanceId, setGoogleInstanceId] = useState('');
  const [googleDatabaseName, setGoogleDatabaseName] = useState('');

  // Direct DB form state
  const [directName, setDirectName] = useState('');
  const [directHost, setDirectHost] = useState('');
  const [directPort, setDirectPort] = useState('5432');
  const [directUser, setDirectUser] = useState('');
  const [directPassword, setDirectPassword] = useState('');

  const handleSubmitGoogle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googleName || !googleProjectId) {
      toast.error(t('googleCloud.nameProjectRequired'));
      return;
    }

    await mcpCall('google-cloud.connect', {
      name: googleName,
      type: 'google-cloud',
      project_id: googleProjectId,
      region: googleRegion,
      instance_id: googleInstanceId || undefined,
      database_name: googleDatabaseName || undefined,
    });

    onClose();
  };

  const handleSubmitDirect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!directName || !directHost || !directPort || !directUser || !directPassword) {
      toast.error(t('googleCloud.allFieldsRequired'));
      return;
    }

    await mcpCall('google-cloud.connect', {
      name: directName,
      type: 'direct',
      db_host: directHost,
      db_port: Number(directPort),
      db_user: directUser,
      db_password: directPassword,
    });

    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={t('googleCloud.newConnection')} size='lg'>
      <div className='space-y-6'>
        {/* Step 1: Connection Type Selection */}
        {step === 1 && (
          <div className='space-y-4'>
            <div className='flex items-center gap-2 mb-4'>
              <div className='flex-1 h-px bg-gray-700' />
              <span className='text-sm text-gray-500'>{t('googleCloud.step1')}</span>
              <div className='flex-1 h-px bg-gray-700' />
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <button
                type='button'
                onClick={() => setConnectionType('google-cloud')}
                className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all ${
                  connectionType === 'google-cloud'
                    ? 'border-blue-500 bg-blue-900/10'
                    : 'border-gray-700 hover:border-gray-600 bg-gray-900/20'
                }`}
              >
                <Cloud size={32} className='mb-2 text-blue-400' />
                <span className='font-semibold text-white'>Google Cloud</span>
                <span className='text-xs text-gray-500 mt-1 text-center'>
                  {t('googleCloud.googleCloudDesc')}
                </span>
              </button>

              <button
                type='button'
                onClick={() => setConnectionType('direct')}
                className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all ${
                  connectionType === 'direct'
                    ? 'border-green-500 bg-green-900/10'
                    : 'border-gray-700 hover:border-gray-600 bg-gray-900/20'
                }`}
              >
                <Database size={32} className='mb-2 text-green-400' />
                <span className='font-semibold text-white'>{t('googleCloud.directConnection')}</span>
                <span className='text-xs text-gray-500 mt-1 text-center'>
                  {t('googleCloud.directDesc')}
                </span>
              </button>
            </div>

            <div className='flex justify-end pt-4'>
              <Button variant='primary' onClick={() => setStep(2)} icon={<ArrowRight size={16} />}>
                {t('googleCloud.continue')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Form based on connection type */}
        {step === 2 && (
          <div className='space-y-4'>
            <div className='flex items-center gap-2 mb-4'>
              <div className='flex-1 h-px bg-gray-700' />
              <span className='text-sm text-gray-500'>{t('googleCloud.step2')}</span>
              <div className='flex-1 h-px bg-gray-700' />
            </div>

            {connectionType === 'google-cloud'
              ? (
                <form onSubmit={handleSubmitGoogle} className='space-y-4'>
                  <div>
                    <Label>{t('googleCloud.connectionName')}</Label>
                    <Input
                      value={googleName}
                      onChange={(e) => setGoogleName(e.target.value)}
                      placeholder={t('googleCloud.phProdDb')}
                      required
                    />
                  </div>
                  <div>
                    <Label>Google Cloud Project ID</Label>
                    <Input
                      value={googleProjectId}
                      onChange={(e) => setGoogleProjectId(e.target.value)}
                      placeholder='my-project-123'
                      required
                    />
                  </div>
                  <div>
                    <Label>Region</Label>
                    <Input
                      value={googleRegion}
                      onChange={(e) => setGoogleRegion(e.target.value)}
                      list='regions'
                    />
                    <datalist id='regions'>
                      <option value='europe-west1' />
                      <option value='europe-west2' />
                      <option value='europe-west3' />
                      <option value='us-central1' />
                      <option value='us-east1' />
                      <option value='us-west1' />
                    </datalist>
                  </div>
                  <div>
                    <Label>{t('googleCloud.instanceIdOptional')}</Label>
                    <Input
                      value={googleInstanceId}
                      onChange={(e) => setGoogleInstanceId(e.target.value)}
                      placeholder='proj:region:instance'
                    />
                  </div>
                  <div>
                    <Label>{t('googleCloud.dbNameOptional')}</Label>
                    <Input
                      value={googleDatabaseName}
                      onChange={(e) => setGoogleDatabaseName(e.target.value)}
                      placeholder='mydb'
                    />
                  </div>

                  <div className='rounded-lg border border-blue-900 bg-blue-900/10 p-4'>
                    <div className='flex items-start gap-3'>
                      <Cloud size={20} className='text-blue-400 mt-0.5' />
                      <div>
                        <h4 className='font-semibold text-blue-400'>Google Cloud</h4>
                        <p className='text-sm text-blue-300 mt-1'>
                          {t('googleCloud.googleCloudInfo')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='flex justify-between gap-2 pt-4 border-t border-gray-800'>
                    <Button type='button' variant='secondary' onClick={() => setStep(1)}>
                      {t('common.back')}
                    </Button>
                    <Button type='submit' variant='primary'>{t('googleCloud.create')}</Button>
                  </div>
                </form>
              )
              : (
                <form onSubmit={handleSubmitDirect} className='space-y-4'>
                  <div>
                    <Label>{t('googleCloud.connectionName')}</Label>
                    <Input
                      value={directName}
                      onChange={(e) => setDirectName(e.target.value)}
                      placeholder={t('googleCloud.phPostgresProd')}
                      required
                    />
                  </div>
                  <div>
                    <Label>Host</Label>
                    <Input
                      value={directHost}
                      onChange={(e) => setDirectHost(e.target.value)}
                      placeholder='10.0.0.5'
                      required
                    />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input
                      type='number'
                      value={directPort}
                      onChange={(e) => setDirectPort(e.target.value)}
                      placeholder='5432'
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('googleCloud.user')}</Label>
                    <Input
                      value={directUser}
                      onChange={(e) => setDirectUser(e.target.value)}
                      placeholder='postgres'
                      required
                    />
                  </div>
                  <div>
                    <Label>{t('googleCloud.password')}</Label>
                    <Input
                      type='password'
                      value={directPassword}
                      onChange={(e) => setDirectPassword(e.target.value)}
                      placeholder='••••••••'
                      required
                    />
                  </div>

                  <div className='rounded-lg border border-green-900 bg-green-900/10 p-4'>
                    <div className='flex items-start gap-3'>
                      <Database size={20} className='text-green-400 mt-0.5' />
                      <div>
                        <h4 className='font-semibold text-green-400'>{t('googleCloud.directConnection')}</h4>
                        <p className='text-sm text-green-300 mt-1'>
                          {t('googleCloud.directInfo')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='flex justify-between gap-2 pt-4 border-t border-gray-800'>
                    <Button type='button' variant='secondary' onClick={() => setStep(1)}>
                      {t('common.back')}
                    </Button>
                    <Button type='submit' variant='primary'>{t('googleCloud.create')}</Button>
                  </div>
                </form>
              )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Query Tab ──────────────────────────────────────────────────────────────

function QueryTab() {
  const { t } = useTranslation();
  const [connectionId, setConnectionId] = useState('');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<GoogleQueryResult | null>(null);

  const { data: connections } = useQuery({
    queryKey: ['google-cloud-connections'],
    queryFn: () =>
      api.mcp<{ ok: boolean; connections?: GoogleConnection[] }>('google-cloud.list'),
    staleTime: 60_000,
  });

  const queryMut = useMutation({
    mutationFn: (args: { connection_id: string; query: string }) =>
      mcpCall<{ ok: boolean; result: GoogleQueryResult; error?: string }>(
        'google-cloud.query',
        args,
      ),
    onSuccess: (data) => {
      if (data.ok) {
        setResult(data.result);
        toast.success(t('googleCloud.querySuccess', { count: data.result.rowCount }));
      } else {
        toast.error(data.error || t('googleCloud.queryError'));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleRun = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!connectionId || !query) {
      toast.error(t('googleCloud.selectConnAndQuery'));
      return;
    }
    await queryMut.mutateAsync({ connection_id: connectionId, query });
  };

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Terminal}>{t('googleCloud.sqlQuery')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRun} className='space-y-4'>
            <div>
              <Label>{t('googleCloud.connection')}</Label>
              <Select
                value={connectionId}
                onChange={(e) => setConnectionId(e.target.value)}
                className='w-full'
              >
                <option value=''>{t('googleCloud.selectConnection')}</option>
                {connections?.connections?.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.project_id})</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>{t('googleCloud.sqlQuery')}</Label>
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='SELECT * FROM table_name LIMIT 10'
                rows={6}
                className='font-mono text-xs'
              />
            </div>
            <div className='flex gap-2'>
              <Button
                type='submit'
                variant='primary'
                disabled={queryMut.isPending}
                icon={<Terminal size={16} />}
              >
                {t('googleCloud.runQuery')}
              </Button>
              <Button
                type='button'
                variant='secondary'
                onClick={() => {
                  setQuery('');
                  setResult(null);
                }}
              >
                {t('googleCloud.clearButton')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle icon={Database}>
              {t('googleCloud.resultTitle', { count: result.rowCount })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='rounded-xl border border-gray-800 overflow-hidden bg-gray-900/20'>
              <table className='w-full text-left text-xs'>
                <thead className='bg-gray-900/40 border-b border-gray-800'>
                  <tr>
                    {result.columns.map((col, i) => (
                      <th
                        key={i}
                        className='px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider'
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-800'>
                  {result.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className='px-3 py-2 text-gray-300'>
                          {String(cell ?? 'NULL')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function GoogleCloudPage() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='Google Cloud'
        description={t('googleCloud.description')}
        icon={<Cloud size={24} className='text-blue-400' />}
      />

      <Tabs defaultTab='connections'>
        <TabList>
          <Tab id='connections' icon={<Cloud size={16} />}>{t('googleCloud.connectionsTab')}</Tab>
          <Tab id='query' icon={<Terminal size={16} />}>{t('googleCloud.queriesTab')}</Tab>
        </TabList>

        <TabPanel id='connections' keepMounted>
          <ConnectionsTab />
        </TabPanel>

        <TabPanel id='query' keepMounted>
          <QueryTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}
